import React, { useCallback, useEffect, useRef } from 'react'

import { AnimateContainer } from '@/components/commons/container/AnimateContainer.component'

// styles
import { overlay, modal, closeButton, modalContainer } from '../styles'
import { cssClass } from '@/shared/utils'
import { CloseIcon } from 'public/assets/v2/images/icons'
import { createPortal } from 'react-dom'

interface Props {
  children: React.ReactNode
  onClose?: () => void
  className?: string[]
  classNameOverlay?: string[]
  classNameContainer?: string[]
  show?: boolean
  preventCloseOutside?: boolean
  hideCloseIcon?: boolean
  style?: React.CSSProperties
}

export const ModalPage: React.FC<Props> = ({
  children,
  onClose,
  className = [],
  classNameOverlay = [],
  classNameContainer = [],
  show = true,
  preventCloseOutside = false,
  hideCloseIcon = false,
}) => {
  const projectRoot = useRef<HTMLElement>(null)

  const closeModal = useCallback(() => {
    activateScroll()
    onClose && onClose()
  }, [onClose])

  const handleModalClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation()
  }

  const clickOutside = () => {
    if (!preventCloseOutside) {
      document.body.style.overflow = show ? 'hidden' : 'auto'
      closeModal()
    }
  }

  const activateScroll = () => {
    if (typeof document === 'undefined') return
    document.body.style.overflow = 'auto'
  }

  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') return

    window?.addEventListener('keydown', handleEsc)
    document.body.style.overflow = show ? 'hidden' : 'auto'

    return () => {
      if (typeof window === 'undefined' || typeof document === 'undefined')
        return
      window?.removeEventListener('keydown', handleEsc)
      activateScroll()
    }
  }, [show, closeModal])

  // mount the modal in the actual root so that it doesn't change the layout
  useEffect(() => {
    projectRoot.current = document.getElementById('creatorland-root')
  }, [])

  if (!show) return null

  if (projectRoot.current) {
    return createPortal(
      <div onClick={clickOutside}>
        <AnimateContainer
          type="fadeIn"
          active={true}
          setOpacityNone={true}
          manualActive={true}
          className={[overlay, ...classNameOverlay]}
        >
          <div
            onClick={handleModalClick}
            className={cssClass([modalContainer, ...classNameContainer])}
            style={{ minWidth: '100%' }}
          >
            <AnimateContainer
              className={[modal, ...className]}
              type="fadeInTop"
              active={true}
              setOpacityNone={true}
              manualActive={true}
              delay={200}
            >
              {!hideCloseIcon ? (
                <div onClick={closeModal} className={closeButton}>
                  <CloseIcon />
                </div>
              ) : null}

              {children}
            </AnimateContainer>
          </div>
        </AnimateContainer>
      </div>,
      projectRoot.current
    )
  }
  return (
    <div onClick={clickOutside}>
      <AnimateContainer
        type="fadeIn"
        active={true}
        setOpacityNone={true}
        manualActive={true}
        className={[overlay, ...classNameOverlay]}
      >
        <div
          onClick={handleModalClick}
          className={cssClass([modalContainer, ...classNameContainer])}
        >
          <AnimateContainer
            className={[modal, ...className]}
            type="fadeInTop"
            active={true}
            setOpacityNone={true}
            manualActive={true}
            delay={200}
          >
            {!hideCloseIcon ? (
              <div onClick={closeModal} className={closeButton}>
                <CloseIcon />
              </div>
            ) : null}

            {children}
          </AnimateContainer>
        </div>
      </AnimateContainer>
    </div>
  )
}
